import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, User, ChevronRight, Plus, X, Pencil, Trash2, Link, Unlink, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminNotesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [customers, setCustomers] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add/Edit customer modal
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [addCustomerMode, setAddCustomerMode] = useState(null); // 'new' or 'portal'
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    language: 'da'
  });
  const [selectedImportUser, setSelectedImportUser] = useState(null);
  const [selectedImportLanguage, setSelectedImportLanguage] = useState('da');
  const [importSearchQuery, setImportSearchQuery] = useState('');
  
  // Link to portal modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingCustomer, setLinkingCustomer] = useState(null);
  const [selectedPortalUser, setSelectedPortalUser] = useState('');
  const [portalSearchQuery, setPortalSearchQuery] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      // Fetch from note_customers (NOT jcp_customers)
      const response = await axios.get(`${API}/note-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort alphabetically by first name
      const sorted = response.data.sort((a, b) => 
        (a.first_name || '').localeCompare(b.first_name || '', 'da')
      );
      setCustomers(sorted);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Kunne ikke hente kunder');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortalUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Include all users - legacy users without is_approved/is_admin are treated as valid
      // Only exclude users that are explicitly marked as admin
      const validUsers = response.data.filter(u => u.is_admin !== true);
      setPortalUsers(validUsers);
    } catch (error) {
      console.error('Error fetching portal users:', error);
    }
  };

  const openLinkModal = async (customer, e) => {
    e.stopPropagation();
    setLinkingCustomer(customer);
    setSelectedPortalUser(customer.portal_user_id || '');
    setPortalSearchQuery('');
    await fetchPortalUsers();
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinkingCustomer(null);
    setSelectedPortalUser('');
    setPortalSearchQuery('');
  };

  const linkToPortal = async () => {
    if (!selectedPortalUser) {
      toast.error('Vælg en portal-bruger');
      return;
    }

    try {
      await axios.post(`${API}/note-customers/${linkingCustomer.id}/link-portal?portal_user_id=${selectedPortalUser}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kunde linket til portal-bruger');
      closeLinkModal();
      fetchCustomers();
    } catch (error) {
      console.error('Error linking customer:', error);
      toast.error('Kunne ikke linke kunde');
    }
  };

  const unlinkFromPortal = async (customer, e) => {
    e.stopPropagation();
    if (!window.confirm(`Fjern link til portal for ${customer.first_name}?`)) return;

    try {
      await axios.post(`${API}/note-customers/${customer.id}/link-portal?portal_user_id=`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link til portal fjernet');
      fetchCustomers();
    } catch (error) {
      console.error('Error unlinking:', error);
      toast.error('Kunne ikke fjerne link');
    }
  };

  const openAddCustomer = async () => {
    setEditingCustomer(null);
    setAddCustomerMode(null); // Show choice first
    setCustomerForm({ first_name: '', last_name: '', email: '', phone: '', language: 'da' });
    setSelectedImportUser(null);
    setSelectedImportLanguage('da');
    setImportSearchQuery('');
    await fetchPortalUsers(); // Pre-load portal users
    setShowAddCustomer(true);
  };

  const openEditCustomer = (customer, e) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setAddCustomerMode('new'); // Edit mode goes straight to form
    setCustomerForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      language: customer.language || 'da'
    });
    setShowAddCustomer(true);
  };

  const closeModal = () => {
    setShowAddCustomer(false);
    setEditingCustomer(null);
    setAddCustomerMode(null);
    setSelectedImportUser(null);
    setSelectedImportLanguage('da');
    setImportSearchQuery('');
    setCustomerForm({ first_name: '', last_name: '', email: '', phone: '', language: 'da' });
  };

  const importFromPortal = async () => {
    if (!selectedImportUser) {
      toast.error('Vælg en portal-bruger');
      return;
    }

    try {
      // Create note customer with portal user's data
      const data = {
        first_name: selectedImportUser.first_name || '',
        last_name: selectedImportUser.last_name || '',
        email: selectedImportUser.email || null,
        phone: null,
        language: selectedImportLanguage
      };

      const response = await axios.post(`${API}/note-customers`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Immediately link to portal user
      const newCustomerId = response.data.id;
      await axios.post(`${API}/note-customers/${newCustomerId}/link-portal?portal_user_id=${selectedImportUser.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${selectedImportUser.first_name} importeret og linket til portal`);
      closeModal();
      fetchCustomers();
    } catch (error) {
      console.error('Error importing customer:', error);
      toast.error('Kunne ikke importere kunde');
    }
  };

  const saveCustomer = async () => {
    if (!customerForm.first_name.trim()) {
      toast.error('Fornavn er påkrævet');
      return;
    }

    try {
      const data = {
        first_name: customerForm.first_name.trim(),
        last_name: customerForm.last_name.trim() || null,
        email: customerForm.email.trim() || null,
        phone: customerForm.phone.trim() || null,
        language: customerForm.language || 'da'
      };

      if (editingCustomer) {
        await axios.put(`${API}/note-customers/${editingCustomer.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kunde opdateret');
      } else {
        await axios.post(`${API}/note-customers`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kunde tilføjet');
      }

      closeModal();
      fetchCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Kunne ikke gemme kunde');
    }
  };

  const deleteCustomer = async (customer, e) => {
    e.stopPropagation();
    if (!window.confirm(`Slet ${customer.first_name} ${customer.last_name || ''}?\n\nAlle heste og noter for denne kunde vil også blive slettet.`)) return;

    try {
      await axios.delete(`${API}/note-customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kunde slettet');
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Kunne ikke slette kunde');
    }
  };

  // Filter customers by search query
  const filteredCustomers = customers.filter(customer => {
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  // Group customers by first letter of first name
  const groupedCustomers = filteredCustomers.reduce((acc, customer) => {
    const firstLetter = (customer.first_name || '?')[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(customer);
    return acc;
  }, {});

  // Sort letters alphabetically
  const sortedLetters = Object.keys(groupedCustomers).sort((a, b) => a.localeCompare(b, 'da'));

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Indlæser...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' }}
            >
              <ArrowLeft size={24} />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '1.25rem', color: '#333', fontWeight: 600, letterSpacing: '0.05em' }}>
                UNDERVISNINGSNOTER
              </h1>
              <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
                {customers.length} {customers.length === 1 ? 'kunde' : 'kunder'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/admin/ordliste')}
              title="Ordliste til transskribering"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: '#e8f5e9',
                color: '#4caf50',
                border: '1px solid #c8e6c9',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}
            >
              <BookOpen size={16} />
              Ordliste
            </button>
            <button
              onClick={openAddCustomer}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: '#6b5e8e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}
            >
              <Plus size={16} />
              Tilføj kunde
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '1rem', 
          marginBottom: '1.5rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          <div style={{ position: 'relative' }}>
            <Search 
              size={20} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#999' 
              }} 
            />
            <input
              type="text"
              placeholder="Søg efter kunde..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.75rem',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6b5e8e'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
        </div>

        {/* Customer List */}
        {filteredCustomers.length === 0 ? (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '3rem', 
            textAlign: 'center', 
            color: '#666',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            {searchQuery ? (
              <>
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ingen kunder fundet</p>
                <p style={{ fontSize: '0.9rem' }}>Prøv at søge med et andet navn</p>
              </>
            ) : (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👤</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ingen kunder endnu</p>
                <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>Tilføj din første kunde for at begynde</p>
                <button
                  onClick={openAddCustomer}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#6b5e8e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Plus size={18} />
                  Tilføj første kunde
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {sortedLetters.map(letter => (
              <div key={letter}>
                {/* Letter header */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  marginBottom: '0.5rem',
                  paddingLeft: '0.5rem'
                }}>
                  <div style={{
                    background: '#6b5e8e',
                    color: 'white',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}>
                    {letter}
                  </div>
                  <span style={{ color: '#999', fontSize: '0.85rem' }}>
                    {groupedCustomers[letter].length} {groupedCustomers[letter].length === 1 ? 'kunde' : 'kunder'}
                  </span>
                </div>
                
                {/* Customers in this letter group */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                }}>
                  {groupedCustomers[letter].map((customer, index) => (
                    <div
                      key={customer.id}
                      onClick={() => navigate(`/admin/noter/${customer.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '1rem',
                        cursor: 'pointer',
                        borderBottom: index < groupedCustomers[letter].length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#f9f9f9'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#f0eef5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '0.75rem',
                        color: '#6b5e8e'
                      }}>
                        <User size={20} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, color: '#333' }}>
                          {customer.first_name} {customer.last_name || ''}
                          {customer.language && (
                            <span style={{ marginLeft: '0.5rem' }}>
                              {customer.language === 'da' ? '🇩🇰' : customer.language === 'de' ? '🇩🇪' : '🇬🇧'}
                            </span>
                          )}
                        </div>
                        {(customer.email || customer.phone) && (
                          <div style={{ fontSize: '0.85rem', color: '#999' }}>
                            {[customer.email, customer.phone].filter(Boolean).join(' • ')}
                          </div>
                        )}
                        {customer.portal_user_id && (
                          <div style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '0.25rem',
                            padding: '0.15rem 0.5rem',
                            background: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            <Link size={12} />
                            Portal-bruger
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Link/Unlink to portal button */}
                        {customer.portal_user_id ? (
                          <button
                            onClick={(e) => unlinkFromPortal(customer, e)}
                            title="Fjern link til portal"
                            style={{
                              background: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.4rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Unlink size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => openLinkModal(customer, e)}
                            title="Link til portal-bruger"
                            style={{
                              background: '#f5f5f5',
                              color: '#666',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.4rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Link size={16} />
                          </button>
                        )}
                        <button
                          onClick={(e) => openEditCustomer(customer, e)}
                          title="Rediger kunde"
                          style={{
                            background: '#fff3e0',
                            color: '#f57c00',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={(e) => deleteCustomer(customer, e)}
                          title="Slet kunde"
                          style={{
                            background: '#ffebee',
                            color: '#c62828',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                        <ChevronRight size={20} color="#ccc" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {customers.length > 0 && (
          <div style={{ 
            marginTop: '1.5rem', 
            textAlign: 'center', 
            color: '#999', 
            fontSize: '0.85rem' 
          }}>
            {filteredCustomers.length} af {customers.length} kunder
          </div>
        )}

        {/* Add/Edit Customer Modal */}
        {showAddCustomer && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
                  {editingCustomer ? 'Rediger kunde' : addCustomerMode === 'new' ? 'Ny kunde' : addCustomerMode === 'portal' ? 'Vælg fra portal' : 'Tilføj kunde'}
                </h2>
                <button
                  onClick={closeModal}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Step 1: Choose mode (only if not editing) */}
              {!editingCustomer && !addCustomerMode && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <button
                    onClick={() => setAddCustomerMode('portal')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1.25rem',
                      background: '#f8f7fc',
                      border: '2px solid #e8e4f0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = '#6b5e8e'; e.currentTarget.style.background = '#f0eef5'; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e8e4f0'; e.currentTarget.style.background = '#f8f7fc'; }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #6b5e8e 0%, #8a7aad 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <Link size={24} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#333', marginBottom: '0.25rem' }}>
                        Vælg fra portal
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Importer en eksisterende portal-bruger
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setAddCustomerMode('new')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1.25rem',
                      background: '#f9f9f9',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = '#999'; e.currentTarget.style.background = '#f5f5f5'; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#f9f9f9'; }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666'
                    }}>
                      <Plus size={24} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#333', marginBottom: '0.25rem' }}>
                        Opret ny kunde
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Tilføj en kunde der ikke er på portalen
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Step 2a: Import from portal */}
              {!editingCustomer && addCustomerMode === 'portal' && (
                <div>
                  <button
                    onClick={() => setAddCustomerMode(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b5e8e',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    ← Tilbage
                  </button>

                  {/* Search */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ position: 'relative' }}>
                      <Search 
                        size={18} 
                        style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} 
                      />
                      <input
                        type="text"
                        placeholder="Søg efter portal-bruger..."
                        value={importSearchQuery}
                        onChange={(e) => setImportSearchQuery(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>

                  {/* Portal users list */}
                  <div style={{ 
                    maxHeight: '300px', 
                    overflow: 'auto', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    {portalUsers
                      .filter(u => {
                        const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
                        return fullName.includes(importSearchQuery.toLowerCase());
                      })
                      .map(user => (
                        <div
                          key={user.id}
                          onClick={() => setSelectedImportUser(user)}
                          style={{
                            padding: '0.85rem 1rem',
                            cursor: 'pointer',
                            background: selectedImportUser?.id === user.id ? '#f0eef5' : 'white',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            borderLeft: selectedImportUser?.id === user.id ? '3px solid #6b5e8e' : '3px solid transparent'
                          }}
                        >
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: selectedImportUser?.id === user.id ? '#6b5e8e' : '#f0f0f0',
                            color: selectedImportUser?.id === user.id ? 'white' : '#666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <User size={18} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, color: '#333' }}>
                              {user.first_name || ''} {user.last_name || ''}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#999' }}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      ))
                    }
                    {portalUsers.filter(u => {
                      const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
                      return fullName.includes(importSearchQuery.toLowerCase());
                    }).length === 0 && (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                        Ingen portal-brugere fundet
                      </div>
                    )}
                  </div>

                  {/* Selected user preview */}
                  {selectedImportUser && (
                    <div style={{
                      padding: '1rem',
                      background: '#f8f7fc',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>Vil importere:</div>
                      <div style={{ fontWeight: 600, color: '#333' }}>
                        {selectedImportUser.first_name} {selectedImportUser.last_name}
                      </div>
                      {selectedImportUser.email && (
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>{selectedImportUser.email}</div>
                      )}
                    </div>
                  )}

                  {/* Language selection */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                      Undervisningssprog
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {[
                        { code: 'da', flag: '🇩🇰', name: 'Dansk' },
                        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
                        { code: 'en', flag: '🇬🇧', name: 'English' }
                      ].map(lang => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setSelectedImportLanguage(lang.code)}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: selectedImportLanguage === lang.code ? '2px solid #6b5e8e' : '2px solid #e0e0e0',
                            borderRadius: '8px',
                            background: selectedImportLanguage === lang.code ? '#f8f7fc' : 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <span style={{ fontSize: '1.5rem' }}>{lang.flag}</span>
                          <span style={{ fontSize: '0.8rem', color: selectedImportLanguage === lang.code ? '#6b5e8e' : '#666' }}>
                            {lang.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      onClick={closeModal}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#f5f5f5',
                        color: '#666',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Annuller
                    </button>
                    <button
                      onClick={importFromPortal}
                      disabled={!selectedImportUser}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: selectedImportUser ? '#6b5e8e' : '#ccc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: selectedImportUser ? 'pointer' : 'not-allowed',
                        fontWeight: 500
                      }}
                    >
                      Importer kunde
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2b: New customer form (or editing) */}
              {(editingCustomer || addCustomerMode === 'new') && (
                <div>
                  {!editingCustomer && (
                    <button
                      onClick={() => setAddCustomerMode(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b5e8e',
                        fontSize: '0.9rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      ← Tilbage
                    </button>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* First name - Required */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                        Fornavn *
                      </label>
                      <input
                        type="text"
                        value={customerForm.first_name}
                        onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })}
                        placeholder="F.eks. Anna"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    {/* Last name - Optional */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                        Efternavn (valgfri)
                      </label>
                      <input
                        type="text"
                        value={customerForm.last_name}
                        onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })}
                        placeholder="F.eks. Nielsen"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    {/* Email - Optional */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                        Email (valgfri)
                      </label>
                      <input
                        type="email"
                        value={customerForm.email}
                        onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                        placeholder="F.eks. anna@example.com"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    {/* Phone - Optional */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                        Telefon (valgfri)
                      </label>
                      <input
                        type="tel"
                        value={customerForm.phone}
                        onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                        placeholder="F.eks. +45 12 34 56 78"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    {/* Language */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>
                        Undervisningssprog
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {[
                          { code: 'da', flag: '🇩🇰', name: 'Dansk' },
                          { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
                          { code: 'en', flag: '🇬🇧', name: 'English' }
                        ].map(lang => (
                          <button
                            key={lang.code}
                            type="button"
                            onClick={() => setCustomerForm({ ...customerForm, language: lang.code })}
                            style={{
                              flex: 1,
                              padding: '0.6rem',
                              border: customerForm.language === lang.code ? '2px solid #6b5e8e' : '2px solid #e0e0e0',
                              borderRadius: '8px',
                              background: customerForm.language === lang.code ? '#f8f7fc' : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '0.2rem'
                            }}
                          >
                            <span style={{ fontSize: '1.25rem' }}>{lang.flag}</span>
                            <span style={{ fontSize: '0.75rem', color: customerForm.language === lang.code ? '#6b5e8e' : '#666' }}>
                              {lang.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Info text */}
                  {!editingCustomer && (
                    <p style={{ 
                      marginTop: '1rem', 
                      padding: '0.75rem', 
                      background: '#f5f5f5', 
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: '#666'
                    }}>
                      💡 Denne kunde bliver ikke automatisk linket til portalen.
                    </p>
                  )}

                  {/* Buttons */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                    <button
                      onClick={closeModal}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#f5f5f5',
                        color: '#666',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Annuller
                    </button>
                    <button
                      onClick={saveCustomer}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#6b5e8e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      {editingCustomer ? 'Gem ændringer' : 'Tilføj kunde'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Link to Portal Modal */}
      {showLinkModal && linkingCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '450px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
                Link til Portal
              </h2>
              <button
                onClick={closeLinkModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
              >
                <X size={24} />
              </button>
            </div>

            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Link <strong>{linkingCustomer.first_name} {linkingCustomer.last_name || ''}</strong> til en portal-bruger, så de kan se deres noter på deres profil.
            </p>

            {/* Search portal users */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ position: 'relative' }}>
                <Search 
                  size={18} 
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} 
                />
                <input
                  type="text"
                  placeholder="Søg efter portal-bruger..."
                  value={portalSearchQuery}
                  onChange={(e) => setPortalSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.6rem 0.6rem 2.25rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>

            {/* Portal users list */}
            <div style={{ 
              maxHeight: '250px', 
              overflow: 'auto', 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {portalUsers
                .filter(u => {
                  const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
                  return fullName.includes(portalSearchQuery.toLowerCase());
                })
                .map(user => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedPortalUser(user.id)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      background: selectedPortalUser === user.id ? '#e8e4f0' : 'white',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: selectedPortalUser === user.id ? '#6b5e8e' : '#f0f0f0',
                      color: selectedPortalUser === user.id ? 'white' : '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={16} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, color: '#333' }}>
                        {user.first_name} {user.last_name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#999' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                ))
              }
              {portalUsers.filter(u => {
                const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
                return fullName.includes(portalSearchQuery.toLowerCase());
              }).length === 0 && (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                  Ingen portal-brugere fundet
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={closeLinkModal}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#f5f5f5',
                  color: '#666',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Annuller
              </button>
              <button
                onClick={linkToPortal}
                disabled={!selectedPortalUser}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: selectedPortalUser ? '#6b5e8e' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedPortalUser ? 'pointer' : 'not-allowed',
                  fontWeight: 500
                }}
              >
                Link kunde
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotesPage;
